<template>
  <div class="space-y-6">
    <!-- Welcome Banner — HMS style rounded card -->
    <div class="welcome-banner">
      <div class="welcome-banner-bg"></div>
      <div class="welcome-banner-bg2"></div>
      <div class="welcome-content">
        <div>
          <h1 class="welcome-title">Investment Platform Dashboard</h1>
          <p class="welcome-sub">Monitor investments, track users, and manage your platform all in one place.</p>
          <div class="welcome-chips">
            <span class="welcome-chip">
              <v-icon x-small color="white" class="mr-1">mdi-trending-up</v-icon>
              {{ overview.investments?.approval_rate?.toFixed(1) || 0 }}% Approval
            </span>
            <span class="welcome-chip">
              <v-icon x-small color="white" class="mr-1">mdi-account-group</v-icon>
              {{ formatNumber(overview.users?.total || 0) }} Users
            </span>
            <span class="welcome-chip">
              <v-icon x-small color="white" class="mr-1">mdi-package-variant</v-icon>
              {{ overview.products?.active || 0 }} Products
            </span>
          </div>
        </div>
        <div class="welcome-actions">
          <v-select
            v-model="selectedPeriod"
            :items="periodOptions"
            outlined
            dense
            dark
            hide-details
            color="white"
            class="period-select"
            @change="refreshData"
          ></v-select>
          <v-btn color="white" elevation="0" small class="refresh-btn" @click="refreshData" :loading="isLoading">
            <v-icon left small color="#1A3A6B">mdi-refresh</v-icon>
            <span style="color:#1A3A6B">Refresh</span>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Stat Cards — HMS 4-column grid -->
    <div class="stats-grid">
      <div v-for="(stat, i) in statCards" :key="i" class="stat-card-item" :class="stat.cardClass">
        <div class="stat-card-top">
          <div class="stat-icon-box" :class="stat.iconBg">
            <v-icon size="20" :color="stat.iconColor">{{ stat.icon }}</v-icon>
          </div>
          <span v-if="stat.badge" class="stat-badge" :class="stat.badgeBg">{{ stat.badge }}</span>
        </div>
        <p class="stat-value">{{ stat.value }}</p>
        <p class="stat-label">{{ stat.title }}</p>
        <p v-if="stat.sub" class="stat-sub">{{ stat.sub }}</p>
        <v-progress-linear
          v-if="stat.progress !== undefined"
          :value="stat.progress"
          :color="stat.progressColor"
          height="3"
          rounded
          class="mt-2"
        ></v-progress-linear>
      </div>
    </div>

    <!-- Alert Row — quick indicators -->
    <div class="alerts-grid">
      <div v-for="(alert, i) in alertCards" :key="i" class="alert-card" :class="alert.borderClass">
        <div class="alert-icon-box">
          <v-icon size="16" :color="alert.color">{{ alert.icon }}</v-icon>
        </div>
        <div>
          <p class="alert-value">{{ alert.value }}</p>
          <p class="alert-label">{{ alert.title }}</p>
        </div>
      </div>
    </div>

    <!-- Charts Section — 2:1 split -->
    <div class="charts-grid">
      <!-- Investment Trends -->
      <div class="chart-card chart-card--wide">
        <div class="chart-card-header">
          <div>
            <h2 class="chart-title">Investment Trends</h2>
            <p class="chart-sub">Last {{ selectedPeriod }} days performance</p>
          </div>
          <div class="chart-legend">
            <span class="legend-dot" style="background:#1A3A6B"></span>
            <span class="legend-text">Amount</span>
            <span class="legend-dot" style="background:#10B981"></span>
            <span class="legend-text">Count</span>
          </div>
        </div>
        <div class="chart-body">
          <div v-if="isLoading" class="chart-loading">
            <v-progress-circular indeterminate color="primary" size="36" width="3"></v-progress-circular>
          </div>
          <div v-else-if="investmentTrends.length === 0" class="chart-empty">
            <v-icon size="48" color="grey lighten-2">mdi-chart-line-variant</v-icon>
            <p>No data for selected period</p>
          </div>
          <div v-else class="chart-canvas-wrap">
            <canvas ref="investmentChart"></canvas>
          </div>
        </div>
      </div>

      <!-- Investment Status Donut -->
      <div class="chart-card">
        <div class="chart-card-header">
          <div>
            <h2 class="chart-title">Investment Status</h2>
            <p class="chart-sub">Current distribution</p>
          </div>
        </div>
        <div class="chart-body">
          <div v-if="isLoading" class="chart-loading">
            <v-progress-circular indeterminate color="orange" size="36" width="3"></v-progress-circular>
          </div>
          <div v-else>
            <div class="donut-wrap">
              <canvas ref="statusChart"></canvas>
            </div>
            <div class="donut-legend">
              <div class="donut-legend-item">
                <span class="legend-dot" style="background:#10B981"></span>
                <span class="legend-label">Approved</span>
                <span class="legend-count success--text">{{ overview.investments?.approved || 0 }}</span>
              </div>
              <div class="donut-legend-item">
                <span class="legend-dot" style="background:#F59E0B"></span>
                <span class="legend-label">Pending</span>
                <span class="legend-count warning--text">{{ overview.investments?.pending || 0 }}</span>
              </div>
              <div class="donut-legend-item">
                <span class="legend-dot" style="background:#EF4444"></span>
                <span class="legend-label">Rejected</span>
                <span class="legend-count error--text">{{ overview.investments?.rejected || 0 }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Chart from "chart.js/auto";

export default {
  name: "HomePage",
  data() {
    return {
      isLoading: false,
      selectedPeriod: 30,
      periodOptions: [
        { text: "Last 7 days", value: 7 },
        { text: "Last 30 days", value: 30 },
        { text: "Last 90 days", value: 90 },
        { text: "Last 180 days", value: 180 },
      ],
      overview: {},
      investmentTrends: [],
      investmentChart: null,
      statusChart: null,
    };
  },
  computed: {
    statCards() {
      const o = this.overview;
      return [
        {
          title: "Total Users",
          value: this.formatNumber(o.users?.total || 0),
          icon: "mdi-account-group",
          iconBg: "icon-bg-blue",
          iconColor: "#1D4ED8",
          badge: `${o.users?.activity_rate?.toFixed(0) || 0}%`,
          badgeBg: "badge-blue",
          cardClass: "",
        },
        {
          title: "Total Investments",
          value: this.formatCurrency(o.investments?.total_amount || 0),
          icon: "mdi-trending-up",
          iconBg: "icon-bg-green",
          iconColor: "#059669",
          sub: `${this.formatNumber(o.investments?.total_count || 0)} transactions`,
          cardClass: "",
        },
        {
          title: "Wallet Balance",
          value: this.formatCurrency(o.wallet?.total_balance || 0),
          icon: "mdi-wallet",
          iconBg: "icon-bg-cyan",
          iconColor: "#0891B2",
          sub: `Avg: ${this.formatCurrency(o.wallet?.average_balance || 0)}`,
          cardClass: "",
        },
        {
          title: "Funding Progress",
          value: `${o.funding?.progress_percentage?.toFixed(1) || 0}%`,
          icon: "mdi-target",
          iconBg: "icon-bg-purple",
          iconColor: "#7C3AED",
          sub: `${this.formatCurrency(o.funding?.amount_raised || 0)} raised`,
          progress: o.funding?.progress_percentage || 0,
          progressColor: "#7C3AED",
          cardClass: "",
        },
      ];
    },
    alertCards() {
      const o = this.overview;
      return [
        {
          title: "Support Tickets",
          value: this.formatNumber(o.support?.total_tickets || 0),
          icon: "mdi-ticket-account",
          color: "#C2410C",
          borderClass: "alert-orange",
        },
        {
          title: "KYC Completion",
          value: `${o.kyc?.completion_rate?.toFixed(0) || 0}%`,
          icon: "mdi-file-document-check",
          color: "#0F766E",
          borderClass: "alert-teal",
        },
        {
          title: "Active Products",
          value: this.formatNumber(o.products?.active || 0),
          icon: "mdi-package-variant",
          color: "#3730A3",
          borderClass: "alert-indigo",
        },
        {
          title: "Approval Rate",
          value: `${o.investments?.approval_rate?.toFixed(1) || 0}%`,
          icon: "mdi-check-circle",
          color: "#15803D",
          borderClass: "alert-green",
        },
      ];
    },
  },
  mounted() {
    this.loadDashboardData();
  },
  beforeDestroy() {
    this.destroyCharts();
  },
  methods: {
    async loadDashboardData() {
      this.isLoading = true;
      try {
        await Promise.all([this.loadOverview(), this.loadInvestmentTrends()]);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        this.isLoading = false;
        this.$nextTick(() => this.createCharts());
      }
    },
    async loadOverview() {
      try {
        const response = await axios.get("dashboard/overview");
        this.overview = response.data.data || {};
      } catch (error) {
        console.error("Error loading overview:", error);
      }
    },
    async loadInvestmentTrends() {
      try {
        const response = await axios.get(
          `dashboard/investment-trends?period=${this.selectedPeriod}`
        );
        this.investmentTrends = response.data.data || [];
      } catch (error) {
        console.error("Error loading investment trends:", error);
      }
    },
    async refreshData() {
      await this.loadDashboardData();
    },
    createCharts() {
      this.createInvestmentChart();
      this.createStatusChart();
    },
    createInvestmentChart() {
      if (!this.$refs.investmentChart || this.investmentTrends.length === 0)
        return;
      this.destroyInvestmentChart();
      const ctx = this.$refs.investmentChart.getContext("2d");
      const labels = this.investmentTrends.map((item) => {
        const date = new Date(item.date);
        return date.toLocaleDateString("en-IN", {
          month: "short",
          day: "numeric",
        });
      });
      const amounts = this.investmentTrends.map((item) =>
        parseFloat(item.amount || 0)
      );
      const counts = this.investmentTrends.map((item) =>
        parseInt(item.count || 0)
      );
      this.investmentChart = new Chart(ctx, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "Investment Amount (₹)",
              data: amounts,
              borderColor: "#1A3A6B",
              backgroundColor: (context) => {
                const chart = context.chart;
                const { ctx: c, chartArea } = chart;
                if (!chartArea) return "rgba(26,58,107,0.08)";
                const gradient = c.createLinearGradient(
                  0,
                  chartArea.top,
                  0,
                  chartArea.bottom
                );
                gradient.addColorStop(0, "rgba(26,58,107,0.15)");
                gradient.addColorStop(1, "rgba(26,58,107,0.01)");
                return gradient;
              },
              fill: true,
              tension: 0.45,
              borderWidth: 2.5,
              pointBackgroundColor: "#1A3A6B",
              pointBorderColor: "#ffffff",
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 7,
              yAxisID: "y",
            },
            {
              label: "No. of Investments",
              data: counts,
              borderColor: "#10B981",
              backgroundColor: "transparent",
              fill: false,
              tension: 0.45,
              borderWidth: 2.5,
              pointBackgroundColor: "#10B981",
              pointBorderColor: "#ffffff",
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 7,
              borderDash: [6, 3],
              yAxisID: "y1",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { intersect: false, mode: "index" },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: "#ffffff",
              titleColor: "#0f172a",
              bodyColor: "#64748b",
              borderColor: "#e2e8f0",
              borderWidth: 1,
              cornerRadius: 10,
              padding: 12,
              displayColors: true,
              titleFont: { family: "Poppins", weight: "600", size: 13 },
              bodyFont: { family: "Poppins", size: 12 },
              callbacks: {
                label: (context) => {
                  const label = context.dataset.label || "";
                  const val = context.parsed.y;
                  if (label.includes("Amount"))
                    return ` ${label}: ₹${new Intl.NumberFormat("en-IN").format(val)}`;
                  return ` ${label}: ${val}`;
                },
              },
            },
          },
          scales: {
            y: {
              type: "linear",
              display: true,
              position: "left",
              grid: { color: "#f1f5f9", drawBorder: false },
              border: { dash: [4, 4], display: false },
              ticks: {
                font: { family: "Poppins", size: 11 },
                color: "#94a3b8",
                callback: (v) =>
                  "₹" +
                  new Intl.NumberFormat("en-IN", {
                    notation: "compact",
                  }).format(v),
              },
            },
            y1: {
              type: "linear",
              display: true,
              position: "right",
              grid: { drawOnChartArea: false, drawBorder: false },
              border: { display: false },
              ticks: {
                font: { family: "Poppins", size: 11 },
                color: "#94a3b8",
                stepSize: 1,
              },
            },
            x: {
              grid: { display: false },
              border: { display: false },
              ticks: {
                font: { family: "Poppins", size: 11 },
                color: "#94a3b8",
                maxRotation: 0,
              },
            },
          },
        },
      });
    },
    createStatusChart() {
      if (!this.$refs.statusChart || !this.overview.investments) return;
      this.destroyStatusChart();
      const ctx = this.$refs.statusChart.getContext("2d");
      const data = [
        this.overview.investments.approved || 0,
        this.overview.investments.pending || 0,
        this.overview.investments.rejected || 0,
      ];
      this.statusChart = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: ["Approved", "Pending", "Rejected"],
          datasets: [
            {
              data,
              backgroundColor: ["#10B981", "#F59E0B", "#EF4444"],
              hoverBackgroundColor: ["#059669", "#D97706", "#DC2626"],
              borderWidth: 3,
              borderColor: "#ffffff",
              hoverOffset: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "72%",
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: "#ffffff",
              titleColor: "#0f172a",
              bodyColor: "#64748b",
              borderColor: "#e2e8f0",
              borderWidth: 1,
              cornerRadius: 10,
              padding: 12,
              titleFont: { family: "Poppins", weight: "600", size: 13 },
              bodyFont: { family: "Poppins", size: 12 },
              callbacks: {
                label: (context) => {
                  const total = context.dataset.data.reduce(
                    (a, b) => a + b,
                    0
                  );
                  const pct = total
                    ? ((context.parsed / total) * 100).toFixed(1)
                    : 0;
                  return ` ${context.label}: ${context.parsed} (${pct}%)`;
                },
              },
            },
          },
        },
      });
    },
    destroyCharts() {
      this.destroyInvestmentChart();
      this.destroyStatusChart();
    },
    destroyInvestmentChart() {
      if (this.investmentChart) {
        this.investmentChart.destroy();
        this.investmentChart = null;
      }
    },
    destroyStatusChart() {
      if (this.statusChart) {
        this.statusChart.destroy();
        this.statusChart = null;
      }
    },
    formatNumber(number) {
      return new Intl.NumberFormat("en-IN").format(number);
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    },
  },
};
</script>

<style scoped>
/* ── Spacing utility ── */
.space-y-6 > * + * { margin-top: 24px; }

/* ── Welcome Banner — HMS style ── */
.welcome-banner {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  background: linear-gradient(135deg, #1A3A6B 0%, #24568A 100%);
  padding: 28px 32px;
}
.welcome-banner-bg {
  position: absolute;
  top: -40%;
  right: -5%;
  width: 220px;
  height: 220px;
  background: rgba(255,255,255,0.05);
  border-radius: 50%;
}
.welcome-banner-bg2 {
  position: absolute;
  bottom: -30%;
  left: 45%;
  width: 160px;
  height: 160px;
  background: rgba(255,255,255,0.04);
  border-radius: 50%;
}
.welcome-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.welcome-title {
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
}
.welcome-sub {
  font-size: 13px;
  color: rgba(255,255,255,0.7);
  margin: 6px 0 0;
  max-width: 480px;
}
.welcome-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}
.welcome-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(255,255,255,0.1);
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255,255,255,0.9);
}
.welcome-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.period-select {
  max-width: 160px;
}
.period-select >>> .v-input__slot {
  background: rgba(255,255,255,0.12) !important;
  border-color: rgba(255,255,255,0.3) !important;
}
.refresh-btn {
  border-radius: 10px !important;
}

/* ── Stats Grid — 4 columns, HMS style ── */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
@media (min-width: 960px) {
  .stats-grid { grid-template-columns: repeat(4, 1fr); }
}
.stat-card-item {
  background: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  padding: 20px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.stat-card-item:hover {
  box-shadow: 0 8px 24px rgba(0,0,0,0.06);
  transform: translateY(-2px);
}
.stat-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.stat-icon-box {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.icon-bg-blue   { background: #EFF6FF; }
.icon-bg-green  { background: #ECFDF5; }
.icon-bg-cyan   { background: #ECFEFF; }
.icon-bg-purple { background: #FAF5FF; }
.stat-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 999px;
}
.badge-blue {
  background: #EFF6FF;
  color: #1D4ED8;
}
.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
  word-break: break-word;
}
.stat-label {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
}
.stat-sub {
  font-size: 12px;
  color: #94a3b8;
  margin: 4px 0 0;
}

/* ── Alerts Grid — 4 columns ── */
.alerts-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}
@media (min-width: 960px) {
  .alerts-grid { grid-template-columns: repeat(4, 1fr); }
}
.alert-card {
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid;
  transition: box-shadow 0.15s ease;
}
.alert-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
.alert-orange { background: #FFF7ED; border-color: #FED7AA; }
.alert-teal   { background: #F0FDFA; border-color: #99F6E4; }
.alert-indigo { background: #EEF2FF; border-color: #C7D2FE; }
.alert-green  { background: #F0FDF4; border-color: #BBF7D0; }
.alert-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255,255,255,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.alert-value {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
}
.alert-label {
  font-size: 11px;
  color: #64748b;
  margin: 0;
}

/* ── Charts Grid ── */
.charts-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}
@media (min-width: 960px) {
  .charts-grid { grid-template-columns: 2fr 1fr; }
}
.chart-card {
  background: #ffffff;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
}
.chart-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
}
.chart-title {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}
.chart-sub {
  font-size: 12px;
  color: #94a3b8;
  margin: 2px 0 0;
}
.chart-legend {
  display: flex;
  align-items: center;
  gap: 6px;
}
.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.legend-text {
  font-size: 11px;
  color: #64748b;
  margin-right: 8px;
}
.chart-body {
  flex: 1;
  padding: 0 24px 24px;
}
.chart-canvas-wrap {
  height: 280px;
  position: relative;
}
.chart-canvas-wrap canvas {
  width: 100% !important;
  height: 100% !important;
}
.chart-loading, .chart-empty {
  height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}
.chart-empty p { font-size: 13px; margin-top: 12px; }

/* ── Donut ── */
.donut-wrap {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.donut-wrap canvas {
  max-height: 170px !important;
  max-width: 170px !important;
}
.donut-legend {
  background: #f8fafc;
  border-radius: 10px;
  padding: 12px 16px;
  margin-top: 16px;
}
.donut-legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}
.donut-legend-item + .donut-legend-item { border-top: 1px solid #f1f5f9; }
.legend-label {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}
.legend-count {
  font-size: 16px;
  font-weight: 700;
}

/* ── Mobile ── */
@media (max-width: 600px) {
  .welcome-banner { padding: 20px; }
  .welcome-title { font-size: 18px; }
  .welcome-content { flex-direction: column; align-items: flex-start; }
  .welcome-actions { width: 100%; }
  .period-select { flex: 1; max-width: none; }
  .stat-card-item { padding: 16px; }
  .stat-value { font-size: 20px; }
  .chart-card-header { padding: 16px 16px 12px; }
  .chart-body { padding: 0 16px 16px; }
  .chart-canvas-wrap { height: 220px; }
  .chart-legend { display: none; }
}
</style>
